import React, { FC, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

interface CopyableCodeProps {
  code: string
  className?: string
}

/**
 * コード表示とコピー機能を提供するコンポーネント
 *
 * このコンポーネントは、コードブロック内にコードを表示し、
 * ワンクリックでクリップボードにコピーする機能を提供します。
 * コピー後は視覚的なフィードバックをユーザーに表示します。
 *
 * @param {string} code - 表示およびコピー対象のコード文字列
 * @param {string} [className=''] - 親要素に付与する追加のCSSクラス名
 *
 * @example
 * // APIキーを表示してコピー可能にする
 * <CopyableCode code={apiKey} />
 *
 * @returns コード表示とコピーボタンを含むUIコンポーネント
 */
const CopyableCode: FC<CopyableCodeProps> = ({ code, className = '' }) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`copyable-code-container ${className}`}>
      <div className="api-key-display">
        <pre className="code-block">
          <code>{code}</code>
        </pre>

        <CopyToClipboard
          text={code}
          onCopy={handleCopy}
        >
          <button
            type="button"
            className={`copy-button ${copied ? 'copied' : ''}`}
            title={t('common.copyable_code.instruction')}
          >
            {
              copied ? (
                <>
                  <FontAwesomeIcon icon={['fas', 'check']} />
                  {t('common.copyable_code.copied')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={['far', 'copy']} />
                  {t('common.copyable_code.copy')}
                </>
              )
            }
          </button>
        </CopyToClipboard>
      </div>
    </div>
  )
}

CopyableCode.defaultProps = {
  className: "",
}

export default CopyableCode
